import { Calendar, Ticket, Trophy } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { Badge } from '@kit/ui/badge';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import type { GlobalSearchResult } from './global-search-service';
import { resultImageSrc } from './result-link';

/**
 * Entity-aware leading visual + trailing tag for a search result row, shared
 * by the inline overlay (`global-search-results`) and the `/search` full page
 * (`search-result-row`) so every surface renders each kind the same way
 * (2026-07-05, Mobbin: TIDAL/Suno rich rows).
 *
 * People get a circular avatar; organizations a square logo; opportunities
 * (competitions, auditions, events) a coral-tinted icon tile so they read as
 * actionable, not as another person. Sizes are caller-controlled so the
 * compact overlay (8) and the roomier page rows (11) share one component.
 */

function initials(label: string): string {
  return (
    label
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase() || '?'
  );
}

/** Opportunity-kind icon element, or null for image-backed kinds. */
function opportunityIcon(row: GlobalSearchResult, className: string) {
  if (row.kind === 'event')
    return <Calendar className={className} aria-hidden />;
  if (row.kind === 'audition') {
    return row.isCompetition ? (
      <Trophy className={className} aria-hidden />
    ) : (
      <Ticket className={className} aria-hidden />
    );
  }
  return null;
}

export function ResultLeadingVisual({
  row,
  size = 'sm',
}: {
  row: GlobalSearchResult;
  /** `sm` = overlay (size-9); `md` = /search page (size-11). */
  size?: 'sm' | 'md';
}) {
  const box = size === 'md' ? 'size-11' : 'size-9';
  const icon = size === 'md' ? 'size-5' : 'size-4';
  const oppIcon = opportunityIcon(row, icon);

  if (oppIcon) {
    return (
      <span
        className={cn(
          'bg-primary/10 text-primary flex shrink-0 items-center justify-center rounded-md',
          box,
        )}
      >
        {oppIcon}
      </span>
    );
  }

  const src = resultImageSrc(row);
  // Organizations read as square logos; people stay circular.
  const shape = row.kind === 'organization' ? 'rounded-md' : 'rounded-full';

  return (
    <Avatar className={cn(box, shape, 'shrink-0')}>
      {src ? <AvatarImage src={src} alt="" className={shape} /> : null}
      <AvatarFallback className={cn(shape, size === 'sm' && 'text-xs')}>
        {initials(row.title)}
      </AvatarFallback>
    </Avatar>
  );
}

/** Trailing kind tag — only for opportunity kinds (competition / audition / event). */
export function ResultTag({ row }: { row: GlobalSearchResult }) {
  if (row.kind === 'audition') {
    return (
      <Badge variant="secondary" className="shrink-0">
        <Trans
          i18nKey={
            row.isCompetition ? 'search:tag.competition' : 'search:tag.audition'
          }
        />
      </Badge>
    );
  }
  if (row.kind === 'event') {
    return (
      <Badge variant="secondary" className="shrink-0">
        <Trans i18nKey="search:tag.event" defaults="Event" />
      </Badge>
    );
  }
  return null;
}
